import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import IconChevronRight from '@assets/Icon/svg/IconChevronRight';
import IconBPMultiplier from '@assets/Icon/svg/BPMultiplier';

import { colors, Row, Col, Button, Typography } from '@ajaibid/components';
import { formatCurrency } from '@ajaibid/utils';
import stockService from '@services/stock';

import { useStockBuySellContext } from '../../../hooks';
import {
  styMultiplierBadge,
  styFormBpInsufficientRow,
  styFormBpUnavailable,
  styFormMtSuspendedCTA,
} from '../../../style';
import { useEventTracker } from '../../../../tracker';

const INSUFFICIENT_MESSAGES = {
  REGULAR: {
    message: 'Harga saham + biaya melebihi Buying Power. Deposit untuk membeli saham ini.',
    actionText: 'Deposit',
    actionLink: '/rdn',
  },
  DAY_TRADING: {
    message: 'Harga saham + biaya melebihi Buying Power. Deposit untuk membeli saham ini.',
    actionText: 'Deposit',
    actionLink: '/day-trading',
  },
  MARGIN_TRADING: {
    message: 'Harga saham + biaya melebihi Buying Power. Transfer untuk membeli saham ini.',
    actionText: 'Transfer',
    actionLink: '/margin-trading',
  },
};

const UNAVAILABLE_MESSAGES = {
  REGULAR: {},
  DAY_TRADING: {},
  MARGIN_TRADING: {
    bpLabel: (
      <Button
        type="primary"
        size="xs"
        disabled
        style={{ fontWeight: 400 }}
        data-testid="txt-bpUnavailable"
      >
        Tidak Tersedia
      </Button>
    ),
    actionText: 'Lihat Detail Margin',
    actionLink: '/margin-trading',
  },
};

const SummaryBuyingPower = () => {
  const navigate = useNavigate();

  const { formState, formKey, isAccountMT, maxBuy } = useStockBuySellContext();

  const { isMtBuySuspended } = stockService.account.mt.status.useMtAccountStatus();
  const { isMtRatioExceeded } = stockService.account.mt.ratio.useMtRatio();

  const { eventViewInsufficientBuy, eventClickInsufficientBuy } = useEventTracker();

  const accountType = formState[formKey.INPUT_ACCOUNT_TYPE]?.value;
  const accountMultiplier = formState[formKey.INPUT_ACCOUNT_TYPE]?.multiplier;

  const isBuyInsufficient = formState[formKey.CALC_ORDER_TOTAL] > maxBuy.value;
  const isMtTradeNotAllowed = isMtBuySuspended || isMtRatioExceeded;

  const isBpUnavailable = isAccountMT && isMtTradeNotAllowed;

  useEffect(() => {
    if (isBuyInsufficient) {
      eventViewInsufficientBuy();
    }
  }, [isBuyInsufficient]);

  return (
    <>
      <Row noGutters align="between">
        <Col size="auto" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Typography type="body" color={colors.NEUTRAL_7} margin={0}>
            Buying Power
          </Typography>
          {accountMultiplier && (
            <span className={styMultiplierBadge({ type: accountType })}>
              <IconBPMultiplier size={12} />
              {`${accountMultiplier}x`}
            </span>
          )}
        </Col>
        <Col size="auto">
          {isBpUnavailable ? (
            UNAVAILABLE_MESSAGES[accountType].bpLabel
          ) : (
            <Typography type="body" margin={0} data-testid="txt-buyingPower">
              {formatCurrency(maxBuy.label, 'IDR')}
            </Typography>
          )}
        </Col>
      </Row>
      <div
        className={styFormBpInsufficientRow({ show: isBuyInsufficient })}
        data-testid="div-bpInsufficient"
      >
        <Row noGutters align="between">
          <Col size="auto">
            <Typography type="alert" alertType="error" margin={0} data-testid="txt-bpInsufficient">
              {INSUFFICIENT_MESSAGES[accountType].message}
            </Typography>
          </Col>
          <Col size="auto">
            <Button
              variant="secondary"
              onClick={() => {
                eventClickInsufficientBuy();
                navigate(INSUFFICIENT_MESSAGES[accountType].actionLink);
              }}
              data-testid="btn-bpInsufficient"
            >
              {INSUFFICIENT_MESSAGES[accountType].actionText}
            </Button>
          </Col>
        </Row>
      </div>
      <div
        className={styFormBpUnavailable({ show: isBpUnavailable })}
        data-testid="div-bpUnavailable"
      >
        <Row noGutters align="right">
          <Col size="auto">
            <Typography
              type="link"
              size="sm"
              className={styFormMtSuspendedCTA}
              onClick={() => navigate(UNAVAILABLE_MESSAGES[accountType].actionLink)}
              data-testid="btn-bpUnavailable"
            >
              {UNAVAILABLE_MESSAGES[accountType].actionText}
              <IconChevronRight />
            </Typography>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default SummaryBuyingPower;
